/**
 * Credits V2
 *
 * Guthaben & Abonnements Seite.
 * - Tab 1: Credit Packages (Starter Tier - credit-based)
 * - Tab 2: Dimension Pricing (Pro+ Tiers - dimension-based)
 * - Stripe Checkout Aufladung
 * - Transaktionsverlauf
 * - Stripe Checkout Callback Handler
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 * Supports Light and Dark Theme
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { getPlans } from '@/lib/api/credits';
import { Loading, Alert, AlertDescription } from '@/components/v2/ui';
import { PricingTiersGrid } from './credits/PricingTiersGrid';
import { TransactionsSection } from './credits/TransactionsSection';
import { SubscriptionConfigCard } from './credits/SubscriptionConfigCard';
import { CreditTopUpSection } from './credits/CreditTopUpSection';
import { usePaymentReturn } from './credits/hooks/usePaymentReturn';

export function Credits() {
  const { t } = useTranslation();
  const { user } = useSupabaseAuth();
  const queryClient = useQueryClient();

  // Stripe Checkout Return Handler
  const { isProcessing: paymentProcessing } = usePaymentReturn(() => {
    // Invalidate v2 subscription & transaction queries so child components refetch
    queryClient.invalidateQueries({ queryKey: ['active-subscription'] });
    queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
    queryClient.invalidateQueries({ queryKey: ['subscription-usage'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['credit-topup-packages'] });
  });

  // Load Plans
  const { isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
    staleTime: 1000 * 60 * 60, // 1 hour cache
    retry: 2,
  });

  const isLoading = plansLoading;
  const hasErrors = plansError;

  if (isLoading || paymentProcessing) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loading size="lg" />
            <p className="mt-4 text-neutral-600 dark:text-neutral-400">
              {paymentProcessing ? t('billing.processingPayment') : t('common.loading')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const canManage = user?.can_manage_credits || false;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="flex items-center gap-3 text-3xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
              <Wallet className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            {t('billing.creditsAndSubscriptions')}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t('billing.manageCredits')}
          </p>
        </div>

        {/* Permission Warning */}
        {!canManage && (
          <Alert variant="warning" className="mb-8">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('billing.restrictedPermissionsNote')}
            </AlertDescription>
          </Alert>
        )}

        {/* API Error Warning */}
        {hasErrors && (
          <Alert variant="warning" className="mb-8">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{t('billing.connectionError')}</strong> {t('billing.someDataNotLoaded')}
              {plansError && <div className="text-xs mt-1">{String(plansError)}</div>}
              <div className="text-xs mt-2">{t('billing.fallbackDataShown')}</div>
            </AlertDescription>
          </Alert>
        )}

        {/* Active Subscription Status */}
        {user && (
          <div className="mb-8">
            <SubscriptionConfigCard />
          </div>
        )}

        {/* Pricing Tiers Grid */}
        <div className="mb-12">
          <PricingTiersGrid />
        </div>

        {/* Credit Top-Up Section */}
        <div className="mb-12">
          <CreditTopUpSection canManage={canManage} />
        </div>

        {/* Transactions Section */}
        <div>
          <TransactionsSection canManageCredits={canManage} />
        </div>
      </div>
    </div>
  );
}
